import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { App, Button, Card, Progress, Segmented, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { ReloadOutlined, ScanOutlined } from '@ant-design/icons';
import { CONTACT_ISSUES, CONTACT_ISSUE_CODES, type ContactIssue, type ContactIssueCode, type IssueSeverity, type OddContact } from '@crm/shared';
import { PageHeader } from '../../components/PageHeader';
import { CardTitle } from '../../components/StatCard';
import { NameCell, TableSearch } from '../../components/NameCell';
import { BulkActionsBar } from '../../components/BulkActionsBar';
import { EmptyState } from '../../components/states';
import { errorMessage } from '../../api/client';
import { useFields } from '../../features/fields/api';
import { useOddContactsResults, useRecheckOddContacts, useScanOddContacts } from '../../features/cleanup/api';
import { fmtDateTime, plural } from '../../utils/format';

const SEVERITY_COLOR: Record<IssueSeverity, string> = { high: 'error', medium: 'warning', low: 'default' };
const SEVERITY_LABEL: Record<IssueSeverity, string> = { high: 'High', medium: 'Medium', low: 'Low' };
const SEVERITY_ORDER: Record<IssueSeverity, number> = { high: 0, medium: 1, low: 2 };

const severityOf = (code: ContactIssueCode): IssueSeverity => CONTACT_ISSUES[code].severity;
const worst = (c: OddContact) => Math.min(...c.issues.map((i) => SEVERITY_ORDER[severityOf(i.code)]));

/** One tag per issue type; the tooltip lists every field it was found in. */
function IssueTag({ code, found = [] }: { code: ContactIssueCode; found?: ContactIssue[] }) {
  const def = CONTACT_ISSUES[code];
  return (
    <Tooltip
      title={
        <div>
          <div>{def.description}</div>
          {found
            .filter((i) => i.field && i.value !== undefined)
            .map((i) => (
              <div key={i.field} style={{ marginTop: 4 }}>
                {i.field}: <b>"{i.value}"</b>
              </div>
            ))}
        </div>
      }
    >
      <Tag color={SEVERITY_COLOR[def.severity]} style={{ marginBottom: 4 }}>
        {def.label}
      </Tag>
    </Tooltip>
  );
}

/** The contact's issue types, worst first. */
function IssueTags({ issues }: { issues: ContactIssue[] }) {
  const codes = [...new Set(issues.map((i) => i.code))].sort((a, b) => SEVERITY_ORDER[severityOf(a)] - SEVERITY_ORDER[severityOf(b)]);
  return codes.map((code) => <IssueTag key={code} code={code} found={issues.filter((i) => i.code === code)} />);
}

export function OddContactsPage() {
  const { message } = App.useApp();
  const stored = useOddContactsResults();
  const scan = useScanOddContacts();
  const recheck = useRecheckOddContacts();
  const defs = useFields('CONTACT');
  const [progress, setProgress] = useState<{ scanned: number; total: number; found: number } | null>(null);
  const [issueFilter, setIssue] = useState<ContactIssueCode | 'all'>('all');
  const [severity, setSeverity] = useState<IssueSeverity | 'all'>('all');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<string[]>([]);

  const data = stored.data;
  const results = data?.results;

  const runScan = async () => {
    setSelected([]);
    setProgress({ scanned: 0, total: 0, found: 0 });
    try {
      stored.set(await scan.mutateAsync({ onProgress: setProgress }));
    } catch (e) {
      message.error(errorMessage(e));
    } finally {
      setProgress(null);
    }
  };

  /** After bulk actions: fixed or deleted contacts drop out of the results. */
  const runRecheck = async (ids: string[]) => {
    if (!data || !ids.length) return;
    try {
      const fresh = new Map((await recheck.mutateAsync(ids)).map((c) => [c.id, c]));
      const results = data.results.flatMap((c) => (ids.includes(c.id) ? (fresh.has(c.id) ? [fresh.get(c.id)!] : []) : [c]));
      stored.set({ ...data, results });
      setSelected([]);
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  const counts = useMemo(() => {
    const m = new Map<ContactIssueCode, number>();
    for (const c of results ?? []) for (const code of new Set(c.issues.map((i) => i.code))) m.set(code, (m.get(code) ?? 0) + 1);
    return m;
  }, [results]);
  // a filter whose last contact was fixed falls back to all issues
  const issue = issueFilter !== 'all' && counts.has(issueFilter) ? issueFilter : 'all';

  const rows = useMemo(() => {
    const needle = q.toLocaleLowerCase();
    return (results ?? [])
      .filter((c) => issue === 'all' || c.issues.some((i) => i.code === issue))
      .filter((c) => severity === 'all' || c.issues.some((i) => severityOf(i.code) === severity))
      .filter((c) => !needle || c.name.toLocaleLowerCase().includes(needle) || (c.email ?? '').includes(needle))
      .sort((a, b) => worst(a) - worst(b) || b.issues.length - a.issues.length);
  }, [results, issue, severity, q]);

  const percent = progress?.total ? Math.min(99, Math.round((progress.scanned / progress.total) * 100)) : 0;
  const issueCodes = CONTACT_ISSUE_CODES.filter((c) => counts.has(c));

  return (
    <div className="page">
      <PageHeader
        title="Odd contacts"
        subtitle="Find contacts whose names or details look wrong: placeholders, companies in name fields, odd phone numbers and more."
        extra={
          <Button type="primary" icon={data ? <ReloadOutlined /> : <ScanOutlined />} onClick={runScan} loading={scan.isPending}>
            {data ? 'Scan again' : 'Scan contacts'}
          </Button>
        }
      />
      <Space orientation="vertical" size={24} style={{ width: '100%' }}>
        {progress ? (
          <Card title={<CardTitle title="Scanning" subtitle={`${progress.scanned.toLocaleString()} of ${plural(progress.total, 'contact')} checked · ${plural(progress.found, 'odd contact')} so far`} />}>
            <Progress percent={percent} status="active" />
          </Card>
        ) : !data ? (
          <Card title={<CardTitle title="What is checked" subtitle="Hover a check to see what it looks for. Nothing is changed by a scan." />}>
            {(['high', 'medium', 'low'] as const).map((sev) => (
              <div key={sev} style={{ marginBottom: 12 }}>
                <Typography.Text type="secondary" style={{ display: 'inline-block', width: 80 }}>
                  {SEVERITY_LABEL[sev]}
                </Typography.Text>
                {CONTACT_ISSUE_CODES.filter((c) => severityOf(c) === sev).map((c) => (
                  <IssueTag key={c} code={c} />
                ))}
              </div>
            ))}
          </Card>
        ) : (
          <>
            <Card
              title={
                <CardTitle
                  title={results!.length ? `${plural(results!.length, 'odd contact')} found` : 'No odd contacts'}
                  subtitle={`${plural(data.scanned, 'contact')} checked ${fmtDateTime(data.finishedAt)}. Click an issue to show only those contacts.`}
                />
              }
            >
              {issueCodes.length ? (
                <div className="issue-grid">
                  {issueCodes.map((code) => {
                    const def = CONTACT_ISSUES[code];
                    const active = issue === code;
                    return (
                      <Tooltip key={code} title={def.description}>
                        <button type="button" className={`issue-tile${active ? ' issue-tile-active' : ''}`} onClick={() => setIssue(active ? 'all' : code)} aria-pressed={active}>
                          <span className={`issue-dot issue-dot-${def.severity}`} />
                          <span className="issue-tile-label">{def.label}</span>
                          <span className="issue-tile-count">{counts.get(code)!.toLocaleString()}</span>
                        </button>
                      </Tooltip>
                    );
                  })}
                </div>
              ) : (
                <Typography.Text type="secondary">Every contact passed every check.</Typography.Text>
              )}
            </Card>

            {results!.length > 0 && (
              <Card>
                <div className="table-toolbar">
                  <div className="toolbar-group">
                    <TableSearch value={q} onSearch={setQ} placeholder="Search name or email" />
                    <Segmented
                      value={severity}
                      onChange={(v) => setSeverity(v as IssueSeverity | 'all')}
                      options={[
                        { value: 'all', label: 'All' },
                        { value: 'high', label: 'High' },
                        { value: 'medium', label: 'Medium' },
                        { value: 'low', label: 'Low' },
                      ]}
                    />
                    {issue !== 'all' && (
                      <Tag closable onClose={() => setIssue('all')} color="processing">
                        {CONTACT_ISSUES[issue].label}
                      </Tag>
                    )}
                  </div>
                </div>
                {selected.length > 0 && (
                  <div className="bulk-bar" style={{ background: '#EEF2FE', borderRadius: 10, padding: '8px 12px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <BulkActionsBar selection={{ ids: selected }} count={selected.length} contactFields={defs.data ?? []} onDone={() => void runRecheck(selected)} />
                    <Space>
                      <Button size="small" icon={<ReloadOutlined />} loading={recheck.isPending} onClick={() => void runRecheck(selected)}>
                        Recheck
                      </Button>
                      <Button size="small" type="link" onClick={() => setSelected([])}>
                        Clear selection
                      </Button>
                    </Space>
                  </div>
                )}
                <Table<OddContact>
                  rowKey="id"
                  dataSource={rows}
                  rowSelection={{ selectedRowKeys: selected, onChange: (keys) => setSelected(keys as string[]), preserveSelectedRowKeys: true }}
                  pagination={{ defaultPageSize: 25, showSizeChanger: true, showTotal: (n) => plural(n, 'contact') }}
                  locale={{ emptyText: <EmptyState description="No contacts match these filters." /> }}
                  columns={[
                    {
                      title: 'Contact',
                      key: 'contact',
                      width: 320,
                      render: (_, c) => <NameCell name={c.name || '(no name)'} sub={c.email ?? 'No email'} to={`/contacts/${c.id}`} />,
                    },
                    {
                      title: 'Issues',
                      key: 'issues',
                      render: (_, c) => <IssueTags issues={c.issues} />,
                    },
                    {
                      title: '',
                      key: 'open',
                      width: 80,
                      render: (_, c) => <Link to={`/contacts/${c.id}`}>Open</Link>,
                    },
                  ]}
                />
              </Card>
            )}
          </>
        )}
      </Space>
    </div>
  );
}
